import { StripeError } from '@stripe/stripe-js'

import { IRequestOptions, IResponseResult } from 'models/api.model'
import {
  IBaseSubscriptionInfoRaw,
  IExtendedPrimerClientSessionResponseRaw,
  IGiftCardPurchaseResponseRaw,
  IPrimerClientSessionResponseRaw,
  IPrimerPaymentResponseRaw,
  IPurchaseResponseRaw,
  IPurchaseRetrySubscriptionResponseRaw,
  ISecondarySubscriptionResponseRaw,
  ISubscriptionInfoRaw,
  ISubscriptionResponseRaw,
  ISubscriptionUpgradeResponseRaw,
} from 'models/payment.model'

import { ApiService } from 'services/api.service'

import { APP_NAME, Cohort, RequestMethod } from 'root-constants'

export class PaymentApi {
  private readonly baseUrl = 'v2/purchases'
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  createSubscription({
    uuid,
    planId,
    trialPeriodDays,
    promoCode,
    cohort,
  }: {
    uuid: string
    promoCode?: string
    planId: number
    trialPeriodDays: number
    cohort: Cohort
  }): Promise<IResponseResult<ISubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        promo_code: promoCode,
        plan_id: planId,
        trial_duration: trialPeriodDays,
        cohort,
      },
    }

    return this.api.makeRequest<ISubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}`,
      options,
    )
  }

  upgradeSubscription({
    uuid,
    planId,
    cohort,
  }: {
    uuid: string
    planId: number
    cohort: Cohort
  }): Promise<IResponseResult<ISubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        cohort,
      },
    }

    return this.api.makeRequest<ISubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}/upgrade`,
      options,
    )
  }

  upgradePrimerSubscription({
    uuid,
    planId,
    cohort,
  }: {
    uuid: string
    planId: number
    cohort: Cohort
  }): Promise<IResponseResult<ISubscriptionUpgradeResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        cohort,
      },
    }

    return this.api.makeRequest<ISubscriptionUpgradeResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/upgrade`,
      options,
    )
  }

  retryPayment({
    uuid,
    stripeError,
  }: {
    uuid: string
    stripeError: StripeError
  }): Promise<IResponseResult<IPurchaseRetrySubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_error: stripeError,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}/retry`,
      options,
    )
  }

  createPaypalSubscription({
    uuid,
    paypalPlanId,
    cohort,
  }: {
    uuid: string
    paypalPlanId: string | null
    cohort: Cohort
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        paypal_plan_id: paypalPlanId,
        cohort,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/paypal/${uuid}/${APP_NAME}`,
      options,
    )
  }

  getPrimerClientSessionToken({
    uuid,
    planId,
    trialPeriodDays,
    cohort,
  }: {
    uuid: string
    planId: number
    trialPeriodDays?: number
    cohort: string
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        trial_duration: trialPeriodDays,
        cohort,
      },
    }
    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/token`,
      options,
    )
  }

  updatePrimerClientSession({
    uuid,
    paymentId,
    clientToken,
  }: {
    uuid: string
    paymentId?: string
    clientToken: string
  }): Promise<IResponseResult<IExtendedPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.PATCH,
      body: {
        ...(paymentId && { payment_id: paymentId }),
        client_token: clientToken,
      },
    }
    return this.api.makeRequest<IExtendedPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/token`,
      options,
    )
  }

  resumePrimerSubscription({
    uuid,
    paymentId,
    resumeToken,
  }: {
    uuid: string
    paymentId: string
    resumeToken: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
        resume_token: resumeToken,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/payment/resume`,
      options,
    )
  }

  createPrimerSubscription({
    uuid,
    token,
    cohort,
  }: {
    uuid: string
    token: string
    cohort: Cohort
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_method_token: token,
        cohort,
      },
    }
    return this.api.makeRequest<IPurchaseResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/payment`,
      options,
    )
  }

  confirmPrimerSubscription({
    uuid,
    paymentId,
  }: {
    uuid: string
    paymentId: string
  }): Promise<
    IResponseResult<{
      status: boolean
      purchase: ISubscriptionInfoRaw
      error?: boolean
    }>
  > {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${APP_NAME}/confirm`,
      options,
    )
  }

  makeUpsell({
    uuid,
    productId,
    promoCode,
    cohort,
    planId,
  }: {
    uuid: string
    productId: string
    promoCode?: string
    cohort: Cohort
    planId: number
  }): Promise<IResponseResult<ISubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
        cohort,
      },
    }

    return this.api.makeRequest<ISubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${APP_NAME}/upsell`,
      options,
    )
  }

  makePrimerUpsell({
    uuid,
    productId,
    promoCode,
    planId,
    cohort,
  }: {
    uuid: string
    productId: string
    promoCode?: string
    cohort: Cohort
    planId: number
  }): Promise<IResponseResult<IBaseSubscriptionInfoRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
        cohort,
      },
    }

    return this.api.makeRequest<IBaseSubscriptionInfoRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/upsell`,
      options,
    )
  }

  applySecondarySubscription({
    uuid,
    planId,
  }: {
    uuid: string
    planId: number
  }): Promise<IResponseResult<ISecondarySubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
      },
    }

    return this.api.makeRequest<ISecondarySubscriptionResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/secondary`,
      options,
    )
  }

  makeGiftCardUpsell({
    uuid,
    planId,
  }: {
    uuid: string
    planId: number
  }): Promise<IResponseResult<IGiftCardPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
      },
    }

    return this.api.makeRequest<IGiftCardPurchaseResponseRaw>(
      `${this.baseUrl}/${uuid}/${APP_NAME}/gift`,
      options,
    )
  }
}
